import type { BookedVisit } from '@/lib/advisor-api';
import type { VisitItineraryPayload } from '@/lib/visit-itinerary';
import { itineraryRouteProjectIds } from '@/lib/visit-itinerary';

export interface VisitQueueStop {
  project_id: string;
  project_name: string;
}

export interface VisitQueuePayload {
  active?: VisitQueueStop;
  queued: VisitQueueStop[];
  awaiting_confirm?: boolean;
  proposed_label?: string;
}

/** Server route order: active then queued (matches WhatsApp sequential scheduling). */
export function visitRouteProjectIds(queue: VisitQueuePayload): string[] {
  const ids: string[] = [];
  if (queue.active) ids.push(queue.active.project_id);
  for (const q of queue.queued) ids.push(q.project_id);
  return ids;
}

/**
 * Visit board stops — server queue in live mode; demo falls back to engaged exploration.
 */
export function buildVisitRouteStopIds(input: {
  bookedVisits: readonly BookedVisit[];
  visitQueue: VisitQueuePayload | null;
  visitItinerary?: VisitItineraryPayload | null;
  engagedProjectIds: readonly string[];
  visitSkipped: Record<string, boolean>;
  mode: 'demo' | 'live';
}): string[] {
  const booked = input.bookedVisits.map((v) => v.projectId);
  const notSkipped = (id: string) => !input.visitSkipped[id];

  if (input.visitItinerary?.stops.length) {
    const route = itineraryRouteProjectIds(input.visitItinerary).filter(notSkipped);
    return [...new Set([...booked, ...route])];
  }

  if (input.visitQueue) {
    const route = visitRouteProjectIds(input.visitQueue).filter(notSkipped);
    return [...new Set([...booked, ...route])];
  }

  if (input.mode === 'live') {
    return [...new Set(booked.filter(notSkipped))];
  }

  return [
    ...new Set([
      ...booked,
      ...input.engagedProjectIds.filter(notSkipped),
    ]),
  ];
}
